import { traduzirErroSupabase } from '@octawash/shared';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

/**
 * RecuperarSenhaPage — fluxo "esqueci a senha" do cliente.
 *
 * Idioma visual: mesmo do login/signup (bg-octa-hero + 3 blobs morphing + glass card
 * 24px com text-display-md + entrada orquestrada via framer-motion). Single field
 * (email) + CTA hero brand-tinted. Estado de sucesso celebratório com auto-redirect
 * para login em 4s (link manual antes disso).
 */
export function RecuperarSenhaPage() {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [contagem, setContagem] = useState(5);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setCarregando(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    });
    setCarregando(false);
    if (error) {
      toast.error('Falha ao enviar link', { description: traduzirErroSupabase(error) });
      return;
    }
    setEnviado(true);
  }

  // Auto-redirect pra login após 5s no estado de sucesso (countdown visual)
  useEffect(() => {
    if (!enviado) return;
    if (contagem <= 0) {
      navigate('/login', { replace: true });
      return;
    }
    const t = setTimeout(() => setContagem((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [enviado, contagem, navigate]);

  const nomeMarca = tenant?.nom_tenant ?? 'Octa Pontos';
  const iniciais = tenant?.nom_tenant ? nomeMarca.slice(0, 2).toUpperCase() : 'OW';

  // Estado de sucesso celebratório — Mail em ring premium + countdown
  if (enviado) {
    return (
      <div className="bg-octa-hero relative flex min-h-screen items-center justify-center overflow-hidden p-4">
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.55, scale: 1 }}
          transition={{ duration: 1.2 }}
        />
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[24rem] w-[24rem]"
          style={{ animationDelay: '-4s' }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.55, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.15 }}
        />
        <motion.div
          className="relative z-10 w-full max-w-sm text-center"
          initial={{ opacity: 0, y: 20, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <motion.div
            className="bg-octa-gradient shadow-octa-glow mx-auto flex h-24 w-24 items-center justify-center rounded-full ring-4 ring-white/40"
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <Mail className="h-12 w-12 text-white" />
          </motion.div>
          <motion.h1
            className="text-display-md mt-6 font-extrabold tracking-tight"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Link enviado
          </motion.h1>
          <motion.p
            className="text-muted-foreground mt-3 px-2 text-sm"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
          >
            Confira sua caixa de entrada (e o spam, por garantia).
            <br />
            <span className="text-foreground font-semibold">{email}</span>
          </motion.p>
          <motion.div
            className="octa-glass-soft mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85 }}
          >
            <Loader2 className="text-primary h-3.5 w-3.5 animate-spin" />
            <span className="text-muted-foreground">
              Voltando para o login em{' '}
              <span className="text-foreground tabular-nums">{contagem}</span>s
            </span>
          </motion.div>
          <motion.p
            className="mt-3 text-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
          >
            <Link
              to="/login"
              className="text-primary inline-flex items-center gap-1 font-semibold hover:underline"
            >
              Ir agora
              <ArrowRight className="h-3 w-3" />
            </Link>
          </motion.p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-octa-hero relative flex min-h-screen items-center justify-center overflow-hidden p-4">
      {/* Blobs morphing — mesma estética do login (consistência auth) */}
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-gradient pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem]"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.55, scale: 1 }}
        transition={{ duration: 1.2 }}
      />
      <motion.div
        aria-hidden
        className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-32 -right-32 h-[24rem] w-[24rem]"
        style={{ animationDelay: '-4s' }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.55, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.15 }}
      />
      <motion.div
        aria-hidden
        className="octa-blob pointer-events-none absolute right-1/4 top-1/4 h-72 w-72 bg-violet-400"
        style={{ animationDelay: '-8s', opacity: 0.25 }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.25, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
      />

      <div className="relative z-10 w-full max-w-sm space-y-6">
        {/* Logo + título */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.85, y: -6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.34, 1.56, 0.64, 1] }}
        >
          {tenant?.des_logo_url ? (
            <img
              src={tenant.des_logo_url}
              alt={nomeMarca}
              className="shadow-octa-glow octa-float mx-auto mb-4 h-20 w-20 rounded-3xl object-cover ring-4 ring-white/40"
            />
          ) : (
            <div className="bg-octa-gradient shadow-octa-glow octa-float mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-3xl ring-4 ring-white/40">
              <span className="text-2xl font-bold text-white">{iniciais}</span>
            </div>
          )}
          <motion.div
            className="octa-pastel-brand mx-auto mb-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em]"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <ShieldCheck className="h-3 w-3" />
            Recuperação segura
          </motion.div>
          <motion.h1
            className="text-display-md font-extrabold tracking-tight"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Esqueceu a senha?
          </motion.h1>
          <motion.p
            className="text-muted-foreground mt-1.5 px-2 text-sm"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            Sem stress. Manda seu email que mandamos o link.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <Card variant="glass" className="shadow-octa-lg rounded-3xl border-white/40">
            <CardContent className="space-y-5 p-6">
              <form onSubmit={enviar} className="octa-stagger space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider">
                    E-mail da conta
                  </Label>
                  <div className="relative">
                    <Mail
                      aria-hidden
                      className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                    />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      placeholder="seu@email.com"
                      className="h-11 pl-9"
                      // eslint-disable-next-line jsx-a11y/no-autofocus -- formulario unico no fluxo de recuperacao, foco automatico melhora UX
                      autoFocus
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  variant="accent"
                  disabled={carregando || email.length === 0}
                  className="group h-12 w-full rounded-xl text-base font-bold"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {carregando ? (
                      <motion.span
                        key="loading"
                        className="inline-flex items-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Enviando…
                      </motion.span>
                    ) : (
                      <motion.span
                        key="idle"
                        className="inline-flex items-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        Enviar link
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </Button>
              </form>

              <p className="text-muted-foreground text-center text-xs">
                <Link
                  to="/login"
                  className="hover:text-foreground inline-flex items-center gap-1 hover:underline"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Voltar para o login
                </Link>
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.p
          className="text-muted-foreground text-center text-[11px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.75, duration: 0.5 }}
        >
          O link expira em 1 hora. Se não chegar, confira o spam.
        </motion.p>
      </div>
    </div>
  );
}
